import { IntlShape } from 'react-intl';

export enum DocumentType {
  PASSPORT = 'passport',
  ID_CARD = 'id_card',
  DRIVING_LICENSE = 'driving_license',
  CREDIT_DEBIT_CARD = 'credit_or_debit_card',
  SKIP = '',
}

export function getDocumentTypeLabel(type: DocumentType, intl: IntlShape) {
  switch (type) {
    case DocumentType.PASSPORT:
      return intl.formatMessage({ id: 'SHUFTI.ENQUIRIES.DOCUMENT_TYPE_OPTIONS.PASSPORT' });
    case DocumentType.ID_CARD:
      return intl.formatMessage({ id: 'SHUFTI.ENQUIRIES.DOCUMENT_TYPE_OPTIONS.ID_CARD' });
    case DocumentType.DRIVING_LICENSE:
      return intl.formatMessage({ id: 'SHUFTI.ENQUIRIES.DOCUMENT_TYPE_OPTIONS.DRIVING_LICENSE' });
    case DocumentType.CREDIT_DEBIT_CARD:
      return intl.formatMessage({ id: 'SHUFTI.ENQUIRIES.DOCUMENT_TYPE_OPTIONS.CREDIT_DEBIT_CARD' });
    case DocumentType.SKIP:
    default:
      return '—';
  }
}

export function getGenderLabel(gender: string, intl: IntlShape) {
  switch (gender.toLowerCase()) {
    case 'm':
      return intl.formatMessage({ id: 'SHUFTI.ENQUIRIES.GENDER_OPTIONS.MALE' });
    case 'f':
      return intl.formatMessage({ id: 'SHUFTI.ENQUIRIES.GENDER_OPTIONS.FEMALE' });
    case 'o':
      return intl.formatMessage({ id: 'SHUFTI.ENQUIRIES.GENDER_OPTIONS.OTHER' });
    default:
      return '—';
  }
}

export const verificationInstructionIntlIds = {
  allow_paper_based: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.PAPER_BASED',
  allow_photocopy: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.PHOTOCOPY',
  allow_colorcopy: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.COLOR_COPY',
  allow_black_and_white: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.BLACK_AND_WHITE',
  allow_laminated: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.LAMINATED',
  allow_screenshot: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.SCREENSHOT',
  allow_cropped: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.CROPPED',
  allow_scanned: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.SCANNED',
  allow_e_document: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.E_DOCUMENT',
  allow_handwritten_document: 'SHUFTI.ENQUIRIES.DOCUMENT_VARIANTS.HANDWRITTEN_DOCUMENT',
};

export const declinedReasonMap: Record<string, { description: string; category: string }> = {
  SPDR02: {
    description: 'Image of the face not found on the document.',
    category: 'Incomplete Document Data',
  },
  SPDR03: {
    description: 'Image is altered or photoshopped.',
    category: 'Elevated Fraud Risk',
  },
  SPDR04: {
    description: 'Copy of the image found on the web.',
    category: 'Elevated Fraud Risk',
  },
  SPDR05: {
    description: 'Document and Document Two do not belong to the same person.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR06: {
    description: 'Document originality could not be verified.',
    category: 'Elevated Fraud Risk',
  },
  SPDR07: {
    description: "Name on the document doesn't match.",
    category: 'Document Data Mismatch',
  },
  SPDR08: {
    description: "DOB on the document doesn't match.",
    category: 'Document Data Mismatch',
  },
  SPDR09: {
    description: "Date on the document doesn't match.",
    category: 'Document Data Mismatch',
  },
  SPDR10: {
    description: "Issue date on the document doesn't match.",
    category: 'Document Data Mismatch',
  },
  SPDR11: {
    description: "Number on the document doesn't match.",
    category: 'Document Data Mismatch',
  },
  SPDR12: {
    description: 'The issuing country of the document is not supported.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR13: {
    description: "Document doesn't match the provided options.",
    category: 'Unsupported or Invalid Document',
  },
  SPDR14: {
    description: 'Age could not be verified.',
    category: 'Data Validation Issue',
  },
  SPDR15: {
    description: "Face on the document doesn't match with the camera image.",
    category: 'Elevated Fraud Risk',
  },
  SPDR16: {
    description: 'The expiry date of the document does not match the record.',
    category: 'Expired Document',
  },
  SPDR17: {
    description: 'The document is expired.',
    category: 'Expired Document',
  },
  SPDR18: {
    description:
      'The uploaded image of the document is blur, please provide a clear photo of document.',
    category: 'Image Quality Deficiency',
  },
  SPDR21: {
    description: 'Proof and Additional Proof are of different documents.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR24: {
    description: 'Document type is different from the provided options.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR28: {
    description: 'The uploaded image of the document is blurred.',
    category: 'Image Quality Deficiency',
  },
  SPDR36: {
    description: 'Both Documents do not belong to the same person.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR39: {
    description: 'Document doesn’t match the document uploaded at the time of signup.',
    category: 'Elevated Fraud Risk',
  },
  SPDR42: {
    description: 'Front and backside images of the document did not match.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR43: {
    description: 'Camera is not accessible for verification.',
    category: 'Insufficient Submission',
  },
  SPDR44: {
    description: 'Gender could not be verified.',
    category: 'Data Validation Issue',
  },
  SPDR45: {
    description: 'Place of issue could not be verified.',
    category: 'Data Validation Issue',
  },
  SPDR47: {
    description: 'Document proof is a screenshot.',
    category: 'Document Integrity Issue',
  },
  SPDR48: {
    description: 'Document proof is altered/edited.',
    category: 'Elevated Fraud Risk',
  },
  SPDR51: {
    description: 'Document proof is from another screen.',
    category: 'Elevated Fraud Risk',
  },
  SPDR52: {
    description: 'Hologram is missing on the document.',
    category: 'Document Integrity Issue',
  },
  SPDR53: {
    description: 'Document proof is not fully displayed.',
    category: 'Image Quality Deficiency',
  },
  SPDR54: {
    description: 'Document is blurry.',
    category: 'Image Quality Deficiency',
  },
  SPDR55: {
    description: 'Information on the document proof is not visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR56: {
    description: 'Information on the document is edited.',
    category: 'Elevated Fraud Risk',
  },
  SPDR57: {
    description: 'Information on the document is hidden.',
    category: 'Incomplete Document Data',
  },
  SPDR63: {
    description: 'Front and backside images are not of the same document.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR64: {
    description: 'Proof and additional proof do not belong to the same person.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR66: {
    description: 'Both documents should belong to the same person.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR69: {
    description: 'Expiry date does not match with the provided one.',
    category: 'Expired Document',
  },
  SPDR71: {
    description: 'Issue date on the document is not clearly visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR72: {
    description: 'Expiry date on the document is not clearly visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR73: {
    description: 'Date of Birth on the document does not match the provided one.',
    category: 'Document Data Mismatch',
  },
  SPDR74: {
    description: 'Date of Birth on the document is not clearly visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR75: {
    description: 'Name on the document does not match the provided one.',
    category: 'Document Data Mismatch',
  },
  SPDR76: {
    description: 'Name on the document is not clearly visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR77: {
    description: 'Document number does not match the provided one.',
    category: 'Document Data Mismatch',
  },
  SPDR78: {
    description: 'Document number is not clearly visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR79: {
    description: 'Original document number could not be authenticated.',
    category: 'Data Validation Issue',
  },
  SPDR86: {
    description: 'E-document data does not match the provided document proof.',
    category: 'Document Data Mismatch',
  },
  SPDR87: {
    description: 'Face on the E-document does not match the selfie.',
    category: 'Elevated Fraud Risk',
  },
  SPDR89: {
    description: 'Uploaded image of the document is edited or cropped.',
    category: 'Elevated Fraud Risk',
  },
  SPDR90: {
    description: 'Uploaded image is found on the internet.',
    category: 'Elevated Fraud Risk',
  },
  SPDR100: {
    description: 'Picture on the document is not updated.',
    category: 'Incomplete Document Data',
  },
  SPDR103: {
    description: 'The uploaded document does not match the mentioned document type.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR104: {
    description: 'Last name in the uploaded document doesn’t match the record.',
    category: 'Document Data Mismatch',
  },
  SPDR105: {
    description: 'First name in the uploaded document doesn’t match the record.',
    category: 'Document Data Mismatch',
  },
  SPDR111: {
    description: 'Uploaded document is expired.',
    category: 'Expired Document',
  },
  SPDR114: {
    description: 'Gender on the document does not match with the provided gender.',
    category: 'Document Data Mismatch',
  },
  SPDR116: {
    description: 'Gender is not mentioned in the uploaded document.',
    category: 'Incomplete Document Data',
  },
  SPDR117: {
    description: 'Gender is unclear in the uploaded document.',
    category: 'Image Quality Deficiency',
  },
  SPDR118: {
    description: 'The uploaded documents have different names.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR130: {
    description: 'Uploaded image of the document is a screenshot.',
    category: 'Document Integrity Issue',
  },
  SPDR131: {
    description: 'Document is captured from another device.',
    category: 'Elevated Fraud Risk',
  },
  SPDR133: {
    description: 'Document is paperbased or laminated.',
    category: 'Document Integrity Issue',
  },
  SPDR134: {
    description: 'Uploaded image of the document is found on the internet.',
    category: 'Elevated Fraud Risk',
  },
  SPDR135: {
    description: 'Uploaded image is a test card.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR140: {
    description: 'Middle name in the uploaded document doesn’t match the record.',
    category: 'Document Data Mismatch',
  },
  SPDR156: {
    description: 'Expire date is not found on the uploaded document.',
    category: 'Incomplete Document Data',
  },
  SPDR159: {
    description: 'Expiry date of the document is not found.',
    category: 'Incomplete Document Data',
  },
  SPDR165: {
    description: 'Name is not found on the uploaded document.',
    category: 'Incomplete Document Data',
  },
  SPDR166: {
    description: 'Face image doesn’t match the face on the document.',
    category: 'Elevated Fraud Risk',
  },
  SPDR169: {
    description: 'Issue date of the uploaded document doesn’t match the record.',
    category: 'Document Data Mismatch',
  },
  SPDR171: {
    description: 'Expire date of the uploaded document is not visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR174: {
    description: 'Name in the uploaded document is not visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR179: {
    description: 'Name is not found on the document.',
    category: 'Incomplete Document Data',
  },
  SPDR181: {
    description: 'Expiry date of the uploaded document does not match.',
    category: 'Expired Document',
  },
  SPDR187: {
    description: 'Nationality could not be verified.',
    category: 'Data Validation Issue',
  },
  SPDR190: {
    description: 'The provided document is broken.',
    category: 'Document Integrity Issue',
  },
  SPDR193: {
    description: 'The provided document is a photocopy (color or black & white).',
    category: 'Document Integrity Issue',
  },
  SPDR194: {
    description: 'The provided document is edited.',
    category: 'Elevated Fraud Risk',
  },
  SPDR197: {
    description: 'The provided document is scanned.',
    category: 'Document Integrity Issue',
  },
  SPDR200: {
    description: 'The provided document is punched.',
    category: 'Document Integrity Issue',
  },
  SPDR201: {
    description: 'The provided document is cracked.',
    category: 'Document Integrity Issue',
  },
  SPDR202: {
    description: 'The provided document is cropped.',
    category: 'Document Integrity Issue',
  },
  SPDR203: {
    description: 'The provided document is handwritten.',
    category: 'Document Integrity Issue',
  },
  SPDR204: {
    description: 'Document does not belong to GCC countries.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR205: {
    description: 'Document type is not supported.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR206: {
    description: 'Document type is not allowed.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR207: {
    description: 'MRZ not detected on the document.',
    category: 'Document Integrity Issue',
  },
  SPDR208: {
    description: 'Document is not visible or present in the proof.',
    category: 'Image Quality Deficiency',
  },
  SPDR209: {
    description: 'Student card is not acceptable.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR210: {
    description: 'Dual cards detected.',
    category: 'Elevated Fraud Risk',
  },
  SPDR211: {
    description: 'The uploaded document is not supported.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR212: {
    description: "First name on the document doesn't match.",
    category: 'Document Data Mismatch',
  },
  SPDR213: {
    description: 'Middle name on the document doesn’t match.',
    category: 'Document Data Mismatch',
  },
  SPDR214: {
    description: 'Last name on the document doesn’t match.',
    category: 'Document Data Mismatch',
  },
  SPDR215: {
    description: 'The uploaded document is inverted or in mirror view.',
    category: 'Image Quality Deficiency',
  },
  SPDR217: {
    description: 'The same proof are not allowed for document and document two ID Card.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR220: {
    description: 'The document number on the document is invalid.',
    category: 'Data Validation Issue',
  },
  SPDR221: {
    description: 'The gender mentioned on the document does not match the provided information.',
    category: 'Document Data Mismatch',
  },
  SPDR222: {
    description: 'The gender on the document is not clearly visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR223: {
    description: 'The issue date on document is not present.',
    category: 'Incomplete Document Data',
  },
  SPDR224: {
    description: 'The date of birth on document is not clearly visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR226: {
    description: 'The nationality on the document does not match the provided information.',
    category: 'Document Data Mismatch',
  },
  SPDR227: {
    description: 'The nationality on the document is not clearly visible.',
    category: 'Image Quality Deficiency',
  },
  SPDR228: {
    description: 'The nationality on the document is not mentioned.',
    category: 'Incomplete Document Data',
  },
  SPDR229: {
    description: 'The expiry date on document is not present.',
    category: 'Incomplete Document Data',
  },
  SPDR230: {
    description:
      'The uploaded face picture does not match the face photo on the provided document.',
    category: 'Elevated Fraud Risk',
  },
  SPDR232: {
    description: 'The name on document is not present.',
    category: 'Incomplete Document Data',
  },
  SPDR234: {
    description: 'The date of birth on document is not present.',
    category: 'Incomplete Document Data',
  },
  SPDR235: {
    description: 'Face in the provided document is edited.',
    category: 'Elevated Fraud Risk',
  },
  SPDR236: {
    description: 'Font in the provided document is edited.',
    category: 'Elevated Fraud Risk',
  },
  SPDR237: {
    description: 'Background in the provided document is edited.',
    category: 'Elevated Fraud Risk',
  },
  SPDR238: {
    description: 'Text in the provided document is edited.',
    category: 'Elevated Fraud Risk',
  },
  SPDR239: {
    description: 'MRZ in the provided document is edited.',
    category: 'Elevated Fraud Risk',
  },
  SPDR240: {
    description: 'Provided document is edited via applying filters.',
    category: 'Elevated Fraud Risk',
  },
  SPDR241: {
    description: 'The verification process was canceled by the user.',
    category: 'Incomplete Verification',
  },
  SPDR249: {
    description: 'Front or backside proof is not provided.',
    category: 'Incomplete Document Data',
  },
  SPDR251: {
    description: 'Document front proof is not provided.',
    category: 'Incomplete Document Data',
  },
  SPDR252: {
    description: 'Proof and additional proof are of same side of the document.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR261: {
    description: 'MRZ Number on the document does not match.',
    category: 'Document Integrity Issue',
  },
  SPDR262: {
    description: 'Data is hidden on the provided document.',
    category: 'Incomplete Document Data',
  },
  SPDR263: {
    description: 'Uploaded image of the document is pixelated.',
    category: 'Image Quality Deficiency',
  },
  SPDR271: {
    description: 'Frontside of the document is not displayed.',
    category: 'Incomplete Document Data',
  },
  SPDR272: {
    description: 'Backside of the document is not displayed.',
    category: 'Incomplete Document Data',
  },
  SPDR273: {
    description: 'Barcode verification failed.',
    category: 'Document Integrity Issue',
  },
  SPDR274: {
    description: 'End user did not submit complete verification proofs or data.',
    category: 'Insufficient Submission',
  },
  SPDR275: {
    description: 'Face could not be detected OR same side of the document is provided.',
    category: 'Incomplete Document Data',
  },
  SPDR276: {
    description: 'The user does not want to share camera or documents.',
    category: 'Insufficient Submission',
  },
  SPDR284: {
    description: 'The complete verification data was not provided by the user.',
    category: 'Insufficient Submission',
  },
  SPDR285: {
    description: 'Document proofs do not belong to the same person.',
    category: 'Inconsistent Document Proofs',
  },
  SPDR286: {
    description: 'Document proof inconsistent creation and modification dates found.',
    category: 'Elevated Fraud Risk',
  },
  SPDR302: {
    description: 'Name or Address is not present on the provided document.',
    category: 'Incomplete Document Data',
  },
  SPDR305: {
    description: 'Entered personal details do not match with the extracted ID document details.',
    category: 'Elevated Fraud Risk',
  },
  SPDR306: {
    description: 'The thickness of card could not be verified.',
    category: 'Data Validation Issue',
  },
  SPDR312: {
    description: 'Your document cannot be verified due to nationality restrictions.',
    category: 'Unsupported or Invalid Document',
  },
  SPDR319: {
    description: 'The mother name on document is not present.',
    category: 'Incomplete Document Data',
  },
};
